import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { TWCButton } from '@components';

import { assets } from '@assets';
import { onboardingTestIDs, Theme } from '@constants';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';

import { RadialGradientBackground } from '../../components/loading/RadialGradientBackground';
import { TWCPage } from '../../components/primitives/TWCPage/TWCPage';
import { TWCText } from '../../components/primitives/TWCText/TWCText';
import { isWeb } from '../../helpers/Platform';

const tPath = 'errorOnboardingPage.';

export const ErrorPage = ({ route }) => {
  const { errorMessage, buttonMessage } = route?.params ?? {};

  return (
    <TWCPage
      childIds={[onboardingTestIDs.errorPage.tryAgainButton]}
      firstFocus={onboardingTestIDs.errorPage.tryAgainButton}
      pageID={onboardingTestIDs.pages.errorPage}
    >
      <RadialGradientBackground>
        <ErrorPageContent buttonMessage={buttonMessage} errorMessage={errorMessage} />
      </RadialGradientBackground>
    </TWCPage>
  );
};

const ErrorPageContent = ({ errorMessage, buttonMessage }) => {
  const [error, setError] = useState();
  const [accessibilityHint, setAccessibilityHint] = useState();
  const { t } = useTranslation();

  const buttonTitle = t(`${tPath}${buttonMessage ?? 'tryAgainButton'}`);

  useEffect(() => {
    // will need to insert logic to set error from json file based on error message returned
    //from the call once error page is itegrated into Onboarding flow
    if (errorMessage) {
      setError(errorMessage);
      setAccessibilityHint(errorMessage);
    } else {
      setError('defaultErrorMessage');
      setAccessibilityHint('default');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorButtonHandle = useContextNodeHandle(onboardingTestIDs.errorPage.tryAgainButton);

  const noFocus = isWeb ? null : errorButtonHandle;

  const onPress = () => {
    if (errorMessage === 'adobeAuthError') {
      // Navigate to Onboarding home screen
      navigationService.replace(onboardingTestIDs.pages.onboardingHomePage);
      return;
    }

    navigationService.goBack();
  };

  return (
    <View style={Theme.styles.topCenter}>
      <Image source={assets.twcLogo} style={styles.logoImage} testID={onboardingTestIDs.errorPage.logoImage} />
      <Image source={assets.errorSymbol} style={styles.errorSymbol} />
      <TWCText style={styles.headingText} testID={onboardingTestIDs.errorPage.headingText} variant={'mediumText44'}>
        {t(`${tPath}title`)}
      </TWCText>
      <TWCText style={styles.errorText} testID={onboardingTestIDs.errorPage.errorMessage} variant={'regularText24'}>
        {t(`${tPath}errorMsg.${error}`)}
      </TWCText>
      <TWCButton
        accessibilityHint={t(`${tPath}accessibilityHints.${accessibilityHint}`)}
        accessibilityLabel={t(`${tPath}accessibilityLabel`)}
        nextFocusDown={noFocus}
        nextFocusLeft={noFocus}
        nextFocusRight={noFocus}
        nextFocusUp={noFocus}
        style={styles.tryAgainButton}
        styleFocused={styles.tryAgainButtonFocus}
        testID={onboardingTestIDs.errorPage.tryAgainButton}
        textFocusStyle={styles.tryAgainButtonText}
        textStyle={styles.tryAgainButtonText}
        title={buttonTitle}
        onPress={onPress}
      />
      <TWCText
        style={styles.assistanceText}
        testID={onboardingTestIDs.errorPage.assistanceText}
        variant={'regularText20'}
      >
        {t(`${tPath}assistanceText`)}
      </TWCText>
    </View>
  );
};

const styles = StyleSheet.create({
  assistanceText: {
    height: Theme.px(54),
    lineHeight: Theme.px(28),
    textAlign: 'center',
    width: Theme.px(524),
  },
  continueButton: {
    height: Theme.px(48),
    width: Theme.px(540),
  },
  errorSymbol: {
    height: Theme.px(120),
    marginBottom: Theme.px(23),
    width: Theme.px(120),
  },
  errorText: {
    color: Theme.white,
    height: Theme.px(32),
    marginBottom: Theme.px(52),
    textAlign: 'center',
  },
  headingText: {
    color: Theme.white,
    height: Theme.px(58),
    marginBottom: Theme.px(6),
    width: Theme.px(524),
  },
  logoImage: {
    height: Theme.px(156),
    marginBottom: Theme.px(110),
    marginTop: Theme.px(88),
    width: Theme.px(156),
  },
  tryAgainButton: {
    backgroundColor: Theme.dodgerBlue,
    borderRadius: Theme.tileBorderRadius,
    height: Theme.px(70),
    marginBottom: Theme.px(50),
    width: Theme.px(540),
  },
  tryAgainButtonFocus: {
    backgroundColor: Theme.curiousBlue,
  },
  tryAgainButtonText: {
    color: Theme.mainText,
    fontFamily: 'Roboto-Medium',
    fontSize: Theme.px(18),
  },
});
